import { GridRowSelectionModel } from '@mui/x-data-grid';
import React, { createContext, useContext, useState, useRef, ReactNode, useCallback } from 'react';
import { LineItemWithDivision } from '../components/glossary/glossary.type';
import { OCRFixResult, analyzeOCRCurrencyValue } from '../common/utils/common.util';
import { NoteDTO } from 'common/utils/util.type';

export interface CoverPageValues {
  [key: string]: string | number;
}

/** Values captured from a single payapp's line items */
export interface PayappLineValues {
  originalBudget?: number;
  reallocation?: number;
  changeOrder?: number;
  revisedBudget?: number;
  previousApplication?: number;
  completeThisPeriod?: number;
  storedThisPeriod?: number;
  completedStoredToDate?: number;
  balanceToComplete?: number;
  retainage?: number;
}

/**
 * Snapshot of payapp-1 data retained in context so that
 * the dual-payapp merge can combine both payapp values.
 */
export interface FirstPayappSnapshot {
  coverPageValues: CoverPageValues;
  /** keyed by project line-item id (or description key for payapp-only rows) */
  lineItemValuesById: Record<string, PayappLineValues>;
}

interface PayappProcessState {
  activeStep: number;
  parsedTable: any[];
  coverFields: any;
  coverPageValues: CoverPageValues;
  projectId: string;
  projectNumber?: string;
  projectName?: string;
  payappDrawId: string;
  fieldHeadersMapping: Record<string, string>;
  sourceTargetHeadersMapping?: Record<string, string>;
  tableRows: any[];
  selectedRowIds: GridRowSelectionModel;
  projectDraw: any;
  originalProjectDraw: any;
  divisions: any[];
  finalizedParsedRows: any[];
  headersFixed: boolean;
  fixingHeaders: boolean;
  fileUrl: string | null;
  lineItemDivisions: LineItemWithDivision[];
  ocrIssues: Record<string, Record<string, OCRFixResult>>;
  ocrFixedFields: Set<string>;
  ocrIssuesDetected: boolean;
  notes: NoteDTO[];
  /** True when processing the second payapp in a dual-payapp workflow */
  isSecondPayappFlow: boolean;
  /** Retained payapp-1 data used for dual-payapp column merge rules */
  firstPayappSnapshot: FirstPayappSnapshot | null;
  /** Raw payapp-2 table rows (step-2 shape) stored when entering step 3 in second-payapp flow so we can restore step 2 on Back */
  secondPayappTableRows: any[] | null;
  /**
   * The fully-merged payapp-1 project draw (step-4 result).
   * Used as the project-value base when merging payapp-2, so that
   * "project values" in the payapp-2 workflow reflect payapp-1 final data.
   */
  payapp1FinalDraw?: any | null;
  /** True when a skipped payapp has been auto-detected */
  isSkippedPayapp?: boolean;
  /** User's toggle choice: true = apply skipped-payapp handling */
  handleSkippedPayapp?: boolean;
  /** Range of skipped application numbers; null when only one was skipped */
  skippedPayappRange?: { start: number; end: number } | null;
  /** Backup of line items before skipped-payapp values were applied; used to restore when toggling off */
  preSkippedDrawLineItems?: any[] | null;
  /** True when the user chose "Manually Update PayApp" instead of uploading a PDF */
  isManualUpdate?: boolean;
  /** True while a Step 2 subtotal row is in checkbox range-edit mode (PayappTable.tsx) — blocks
   * Back/Next navigation in PayappProcessFooter.tsx until the user confirms or cancels the edit. */
  subtotalRangeEditActive?: boolean;
}

/** A single deferred analytics event accumulated during the payapp workflow. */
export type PendingMergeEvent = {
  eventName: string;
  properties: Record<string, unknown>;
};

export interface SecondPayappUploadResult {
  parsedTable: any[];
  coverFields: any;
  sourceTargetHeadersMapping: Record<string, string>;
  fieldHeadersMapping: Record<string, string>;
  fileUrl: string;
  /** Cover page values already mapped to display labels (same shape as coverPageValues in state) */
  coverPageValues: CoverPageValues;
}

interface PayappProcessContextType {
  state: PayappProcessState;
  setActiveStep: (step: number) => void;
  updateState: (newState: Partial<PayappProcessState>) => void;
  updateCoverPageValue: (field: string, value: string | number) => void;
  updateFieldHeadersMapping: (field: string, newHeader: string) => void;
  setFieldHeadersMapping: (mapping: Record<string, string>) => void;
  updateTableRows: (rows: any[]) => void;
  setSelectedRowIds: (ids: string[]) => void;
  setProjectDraw: (draw: any) => void;
  setFinalizedParsedRows: (rows: any[]) => void;
  setHeadersFixed: (fixed: boolean) => void;
  setFixingHeaders: (fixing: boolean) => void;
  setLineItemDivisions: (divisions: LineItemWithDivision[]) => void;
  detectOCRIssues: () => void;
  fixOCRIssues: () => void;
  getOCRIssueForCell: (rowId: string, fieldName: string) => OCRFixResult | null;
  isCellFixed: (rowId: string, fieldName: string) => boolean;
  getOCRIssuesCount: () => number;
  setNotes: (notes: NoteDTO[]) => void;
  /** Resets state for payapp-2 flow while preserving payapp-1 snapshot */
  resetForSecondPayapp: (snapshot: FirstPayappSnapshot, payapp2Data: SecondPayappUploadResult) => void;
  /** Ref holding deferred merge analytics events — populated during the workflow, flushed at Confirm. */
  pendingMergeEventsRef: React.MutableRefObject<PendingMergeEvent[]>;
  /** Stable UUID identifying this payapp workflow session — attached to every payapp analytics event. */
  payappSessionIdRef: React.MutableRefObject<string>;
}

const PayappProcessContext = createContext<PayappProcessContextType | undefined>(undefined);

interface PayappProcessProviderProps {
  children: ReactNode;
  initialState: Omit<PayappProcessState, 'ocrIssues' | 'ocrFixedFields' | 'ocrIssuesDetected'> & {
    ocrIssues?: Record<string, Record<string, OCRFixResult>>;
    ocrFixedFields?: Set<string>;
    ocrIssuesDetected?: boolean;
  };
}

export const PayappProcessProvider: React.FC<PayappProcessProviderProps> = ({ children, initialState }) => {
  const pendingMergeEventsRef = useRef<PendingMergeEvent[]>([]);
  const payappSessionIdRef = useRef<string>(crypto.randomUUID());

  const [state, setState] = useState<PayappProcessState>({
    ...initialState,
    originalProjectDraw: initialState.projectDraw,
    lineItemDivisions: initialState.lineItemDivisions || [],
    ocrIssues: {},
    ocrFixedFields: new Set(),
    ocrIssuesDetected: false,
    notes: initialState.notes || [],
    isSecondPayappFlow: initialState.isSecondPayappFlow || false,
    firstPayappSnapshot: initialState.firstPayappSnapshot || null,
    secondPayappTableRows: initialState.secondPayappTableRows ?? null,
    payapp1FinalDraw: initialState.payapp1FinalDraw ?? null,
  });

  const setActiveStep = (step: number) => {
    setState(prev => ({ ...prev, activeStep: step }));
  };

  const updateState = (newState: Partial<PayappProcessState>) => {
    setState(prev => ({ ...prev, ...newState }));
  };

  const updateCoverPageValue = (field: string, value: string | number) => {
    setState(prev => ({
      ...prev,
      coverPageValues: {
        ...prev.coverPageValues,
        [field]: value,
      },
    }));
  };

  const updateFieldHeadersMapping = (field: string, newHeader: string) => {
    setState(prev => {
      const updatedMapping = { ...prev.fieldHeadersMapping };

      // Remove any existing mapping that points to this header
      Object.keys(updatedMapping).forEach(key => {
        if (updatedMapping[key] === newHeader && key !== field) {
          delete updatedMapping[key];
        }
      });

      // Add or update the mapping for this field
      if (newHeader) {
        updatedMapping[field] = newHeader;
      } else {
        delete updatedMapping[field];
      }

      return {
        ...prev,
        fieldHeadersMapping: updatedMapping,
      };
    });
  };

  const setFieldHeadersMapping = (mapping: Record<string, string>) => {
    setState(prev => ({
      ...prev,
      fieldHeadersMapping: mapping,
    }));
  };

  const updateTableRows = (rows: any[]) => {
    setState(prev => ({
      ...prev,
      tableRows: rows,
    }));
  };

  const setSelectedRowIds = (ids: string[]) => {
    setState(prev => ({
      ...prev,
      selectedRowIds: ids,
    }));
  };

  const setProjectDraw = (draw: any) => {
    setState(prev => ({
      ...prev,
      projectDraw: draw,
    }));
  };

  const setFinalizedParsedRows = (rows: any[]) => {
    setState(prev => ({
      ...prev,
      finalizedParsedRows: rows,
    }));
  };

  const setHeadersFixed = (fixed: boolean) => {
    setState(prev => ({
      ...prev,
      headersFixed: fixed,
    }));
  };

  const setFixingHeaders = (fixing: boolean) => {
    setState(prev => ({
      ...prev,
      fixingHeaders: fixing,
    }));
  };

  const setLineItemDivisions = (divisions: LineItemWithDivision[]) => {
    setState(prev => ({
      ...prev,
      lineItemDivisions: divisions,
    }));
  };

  const detectOCRIssues = useCallback(() => {
    setState(prev => {
      const newOcrIssues: Record<string, Record<string, OCRFixResult>> = {};
      let hasIssues = false;

      // Helpers for column-level rules (exclude 0s; handle parenthesized negatives)
      const isZeroValue = (trimmed: string): boolean => {
        if (!trimmed) return true;
        let s = trimmed
          .replace(/^\s*\$/, '')
          .replace(/,/g, '')
          .trim();
        if (s.startsWith('(') && s.endsWith(')')) s = s.slice(1, -1).trim();
        return s === '' || s === '0' || /^0*\.?0*$/.test(s);
      };
      const hasTwoDecimalPlaces = (trimmed: string): boolean => {
        if (!trimmed) return false;
        let s = trimmed.trim();
        if (s.startsWith('(') && s.endsWith(')')) s = s.slice(1, -1).trim();
        return /[.,]\d{2}$/.test(s);
      };

      // Column-level stats so we can flag:
      // - leading '$' when most numeric values in that column do NOT start with '$'
      // - values that don't have 2 decimal places when most numeric values do.
      // Exclude 0/$0 from counts so they don't affect majority and aren't flagged as outliers.
      const columnStats: Record<
        string,
        {
          numericCount: number;
          leadingDollarCount: number;
          twoDecimalCount: number;
        }
      > = {};

      // First pass: gather column stats (excluding 0 and $0)
      prev.tableRows.forEach(row => {
        Object.entries(row).forEach(([fieldName, value]) => {
          if (typeof value !== 'string') return;
          const trimmed = value.trim();
          if (!trimmed) return;
          if (isZeroValue(trimmed)) return;

          const hasDigit = /\d/.test(trimmed);
          if (!hasDigit) return;

          const startsWithDollar = trimmed.startsWith('$');
          const twoDecimals = hasTwoDecimalPlaces(trimmed);
          if (!columnStats[fieldName]) {
            columnStats[fieldName] = { numericCount: 0, leadingDollarCount: 0, twoDecimalCount: 0 };
          }
          columnStats[fieldName].numericCount += 1;
          if (startsWithDollar) {
            columnStats[fieldName].leadingDollarCount += 1;
          }
          if (twoDecimals) {
            columnStats[fieldName].twoDecimalCount += 1;
          }
        });
      });

      // Second pass: per-cell OCR plus column-based leading-$ flagging
      prev.tableRows.forEach((row, rowIndex) => {
        const rowId = row.id || `row-${rowIndex}`;
        newOcrIssues[rowId] = {};

        Object.entries(row).forEach(([fieldName, value]) => {
          if (typeof value !== 'string') {
            return;
          }

          const strValue = value as string;
          const trimmed = strValue.trim();

          // Per-cell OCR rules (commas/periods/letters/$-inside/space+2 digits, etc.)
          const ocrResult = analyzeOCRCurrencyValue(strValue, fieldName);
          if (ocrResult.needsFix) {
            newOcrIssues[rowId][fieldName] = ocrResult;
            hasIssues = true;
          }

          // Column-level rule 1: majority of numeric values in this column do NOT start
          // with '$', but some do. Those leading '$' values are likely mis-read and
          // should be highlighted as non-fixable issues.
          const stats = columnStats[fieldName];
          if (
            stats &&
            stats.numericCount > 0 &&
            stats.leadingDollarCount > 0 &&
            stats.leadingDollarCount < stats.numericCount * 0.5 &&
            trimmed.startsWith('$')
          ) {
            if (!newOcrIssues[rowId][fieldName]) {
              newOcrIssues[rowId][fieldName] = {
                originalValue: strValue,
                fixedValue: strValue,
                needsFix: true,
                confidence: 0.7,
                fieldName,
                fixable: false,
              };
              hasIssues = true;
            } else {
              // Cell already has a fixable punctuation issue — mark it so that after the
              // punctuation is auto-fixed the cell remains highlighted for the $ concern.
              newOcrIssues[rowId][fieldName] = {
                ...newOcrIssues[rowId][fieldName],
                hasNonFixableContent: true,
              };
            }
          }

          // Column-level rule 2: majority of numeric values in this column end with
          // exactly 2 decimal places (e.g. 123.45 or (71,452.00)); outliers are
          // highlighted for manual review (non-fixable). Exclude 0/$0 from being
          // flagged; use hasTwoDecimalPlaces so (71,452.00) counts as having 2 decimals.
          if (stats && stats.numericCount > 0 && stats.twoDecimalCount > stats.numericCount * 0.5) {
            if (isZeroValue(trimmed)) {
              // skip: don't flag 0/$0 as missing 2 decimals
            } else {
              const hasDigit = /\d/.test(trimmed);
              const twoDecimals = hasTwoDecimalPlaces(trimmed);
              if (hasDigit && !twoDecimals) {
                if (!newOcrIssues[rowId][fieldName]) {
                  newOcrIssues[rowId][fieldName] = {
                    originalValue: strValue,
                    fixedValue: strValue,
                    needsFix: true,
                    confidence: 0.7,
                    fieldName,
                    fixable: false,
                  };
                  hasIssues = true;
                } else {
                  newOcrIssues[rowId][fieldName] = {
                    ...newOcrIssues[rowId][fieldName],
                    hasNonFixableContent: true,
                  };
                }
              }
            }
          }
        });
      });

      return {
        ...prev,
        ocrIssues: newOcrIssues,
        ocrIssuesDetected: hasIssues,
      };
    });
  }, []);

  const fixOCRIssues = () => {
    setState(prev => {
      const updatedRows = [...prev.tableRows];
      const newOcrFixedFields = new Set(prev.ocrFixedFields);
      const newOcrIssues: Record<string, Record<string, OCRFixResult>> = {};

      Object.entries(prev.ocrIssues).forEach(([rowId, fieldIssues]) => {
        const rowIndex = updatedRows.findIndex(row => (row.id || `row-${updatedRows.indexOf(row)}`) === rowId);
        if (rowIndex === -1) return;

        Object.entries(fieldIssues).forEach(([fieldName, ocrResult]) => {
          // Flag-only issues (fixable === false): keep highlighted, do not change value
          if (ocrResult.fixable === false) {
            if (!newOcrIssues[rowId]) newOcrIssues[rowId] = {};
            newOcrIssues[rowId][fieldName] = ocrResult;
            return;
          }
          if (!newOcrFixedFields.has(`${rowId}:${fieldName}`)) {
            updatedRows[rowIndex] = {
              ...updatedRows[rowIndex],
              [fieldName]: ocrResult.fixedValue,
            };
            newOcrFixedFields.add(`${rowId}:${fieldName}`);
          }

          // Mixed issue: punctuation was fixed, but non-fixable content (e.g. letter/S or
          // misplaced $) still exists and should remain highlighted for manual correction.
          if (ocrResult.hasNonFixableContent) {
            if (!newOcrIssues[rowId]) newOcrIssues[rowId] = {};
            newOcrIssues[rowId][fieldName] = {
              originalValue: ocrResult.fixedValue,
              fixedValue: ocrResult.fixedValue,
              needsFix: true,
              confidence: 0.7,
              fieldName,
              fixable: false,
            };
          }
        });
      });

      return {
        ...prev,
        tableRows: updatedRows,
        ocrFixedFields: newOcrFixedFields,
        ocrIssues: newOcrIssues,
        ocrIssuesDetected: Object.keys(newOcrIssues).length > 0,
      };
    });
  };

  const getOCRIssueForCell = (rowId: string, fieldName: string): OCRFixResult | null => {
    return state.ocrIssues[rowId]?.[fieldName] || null;
  };

  const isCellFixed = (rowId: string, fieldName: string): boolean => {
    return state.ocrFixedFields.has(`${rowId}:${fieldName}`);
  };

  const getOCRIssuesCount = (): number => {
    // Count only fixable issues for the Fix Currency button/badge.
    return Object.values(state.ocrIssues).reduce((total, fieldIssues) => {
      const fixableCount = Object.values(fieldIssues).filter(issue => issue.fixable !== false).length;
      return total + fixableCount;
    }, 0);
  };

  const setNotes = (notes: NoteDTO[]) => {
    setState(prev => ({
      ...prev,
      notes,
    }));
  };

  /**
   * Resets the workflow for the second payapp while persisting the
   * payapp-1 snapshot. Steps 1 & 2 will re-run with payapp-2 data.
   */
  const resetForSecondPayapp = (snapshot: FirstPayappSnapshot, payapp2Data: SecondPayappUploadResult) => {
    setState(prev => ({
      ...prev,
      // Reset workflow to beginning
      activeStep: 0,
      // Replace payapp-1 table / cover data with payapp-2
      parsedTable: payapp2Data.parsedTable,
      coverFields: payapp2Data.coverFields,
      tableRows: payapp2Data.parsedTable,
      fieldHeadersMapping: payapp2Data.fieldHeadersMapping,
      sourceTargetHeadersMapping: payapp2Data.sourceTargetHeadersMapping,
      fileUrl: payapp2Data.fileUrl,
      // Reset per-step derivations
      finalizedParsedRows: [],
      selectedRowIds: [],
      headersFixed: false,
      fixingHeaders: false,
      ocrIssues: {},
      ocrFixedFields: new Set(),
      ocrIssuesDetected: false,
      notes: [],
      // Store payapp-1 final draw as the project base for payapp-2 merging.
      // In payapp-2 workflow "project values" = payapp-1 final step results.
      payapp1FinalDraw: prev.projectDraw,
      // Reset current projectDraw back to the original project so the merge
      // in step 2→3 can start clean (payapp1FinalDraw is used as the merge base).
      projectDraw: prev.projectDraw,
      // Set dual-payapp flags
      isSecondPayappFlow: true,
      firstPayappSnapshot: snapshot,
      secondPayappTableRows: null,
      // Use precomputed cover page values for payapp-2 (already mapped to display labels)
      coverPageValues: payapp2Data.coverPageValues,
    }));
  };

  return (
    <PayappProcessContext.Provider
      value={{
        state,
        setActiveStep,
        updateState,
        updateCoverPageValue,
        updateFieldHeadersMapping,
        setFieldHeadersMapping,
        updateTableRows,
        setSelectedRowIds,
        setProjectDraw,
        setFinalizedParsedRows,
        setHeadersFixed,
        setFixingHeaders,
        setLineItemDivisions,
        detectOCRIssues,
        fixOCRIssues,
        getOCRIssueForCell,
        isCellFixed,
        getOCRIssuesCount,
        setNotes,
        resetForSecondPayapp,
        pendingMergeEventsRef,
        payappSessionIdRef,
      }}
    >
      {children}
    </PayappProcessContext.Provider>
  );
};

export const usePayappProcess = () => {
  const context = useContext(PayappProcessContext);
  if (context === undefined) {
    throw new Error('usePayappProcess must be used within a PayappProcessProvider');
  }
  return context;
};
